/**
 * Rich text for a label's own words (2026-10-28, the inspectors: "very full featured (rich text
 * support) while being easy and simple to use"): a field to type them in, each stretch in its own
 * look — bold, italic, underlined, struck through, a colour of its own — and the row of buttons that
 * gives it. What the field holds is read as runs (`TextRun`), the plan's own form of them.
 *
 * The look is given on the runs themselves, never by the browser's editing commands (each browser
 * writes its own markup for those): what is selected — or, with no caret in the field, all of it —
 * is split out, given the look, and the field drawn again with the selection where it was. Only at a
 * bare caret, where the look is for what is typed next, is it the browser's typing style. The page
 * forbids style attributes, so the field is drawn with elements (<b>, <i>, <u>, <s>) and a colour set
 * on its element's style alone.
 */
import { plainOf as plain, type TextRun } from '../../shared/doc.ts';
import { TEXT_COLORS, textColor } from '../render/rich.ts';
import { h, icon, ICONS, haptic } from './dom.ts';
import { allHave, colourOf, mergeRuns, restyle, type Look } from '../model/runs.ts';

export type { Look };

/* ------------------------------------------------------------ the field's DOM */

/** A colour as the browser writes it (#rrggbb or rgb(r, g, b)) as #rrggbb, or null. */
function hexOf(css: string): string | null {
  const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(css);
  if (m) return '#' + [m[1], m[2], m[3]].map((v) => Number(v).toString(16).padStart(2, '0')).join('');
  return /^#[0-9a-f]{6}$/i.test(css) ? css.toLowerCase() : null;
}
/** A colour as the browser writes it as a stretch's: a key of the palette, or #rrggbb. */
function colourKey(css: string): string | null {
  const hex = hexOf(css);
  if (!hex) return null;
  return TEXT_COLORS.find((c) => c.color.toLowerCase() === hex)?.key ?? hex;
}
const BLOCK = new Set(['DIV', 'P', 'LI']);

/**
 * The field's words walked as runs: each text in the look its elements give it (the nearest that
 * says so), a <br> a line break — but the one that only holds an empty line open, the last of its
 * block — and a block after words a line of its own. `at` maps a place in the DOM (a node and an
 * offset, as a selection says it) to a place in the words.
 */
function walk(root: HTMLElement, at?: { node: Node; offset: number }): { runs: TextRun[]; pos: number } {
  const out: TextRun[] = [];
  // (the field's own colour, given back at a caret, is no colour of its own)
  const own = hexOf(getComputedStyle(root).color);
  let len = 0;
  let pos = -1;
  // a block left: what comes after it is on a line of its own (an empty block is a line of its own too)
  let broken = false;
  const push = (t: string, look: TextRun) => {
    if (!t) return;
    out.push({ ...look, t });
    len += t.length;
  };
  /** The line break a block left owes, given before what follows it (and before any place in it is read). */
  const settle = (look: TextRun) => {
    if (!broken) return;
    broken = false;
    push('\n', look);
  };
  const endsLine = () => len === 0 || (out[out.length - 1]?.t.endsWith('\n') ?? true);
  /** Something follows a <br> in its block (its line, then, is broken there). */
  const followed = (br: Node): boolean => {
    let n: Node | null = br;
    while (n && n !== root) {
      if (n.nextSibling) return true;
      n = n.parentNode;
      if (n instanceof HTMLElement && BLOCK.has(n.tagName)) return false;
    }
    return false;
  };
  const lookOf = (el: HTMLElement, base: TextRun): TextRun => {
    const l: TextRun = { ...base, t: '' };
    const tag = el.tagName;
    if (tag === 'B' || tag === 'STRONG') l.b = true;
    if (tag === 'I' || tag === 'EM') l.i = true;
    if (tag === 'U' || tag === 'INS') l.u = true;
    if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') l.s = true;
    const st = el.style;
    if (st.fontWeight) {
      if (/^(bold|bolder|[6-9]00)$/.test(st.fontWeight) && st.fontWeight !== '600') l.b = true;
      else delete l.b;
    }
    if (st.fontStyle) {
      if (st.fontStyle === 'italic' || st.fontStyle === 'oblique') l.i = true;
      else delete l.i;
    }
    const deco = st.textDecorationLine || st.textDecoration;
    if (deco) {
      if (deco.includes('underline')) l.u = true;
      if (deco.includes('line-through')) l.s = true;
      if (deco === 'none') {
        delete l.u;
        delete l.s;
      }
    }
    const css = st.color || (tag === 'FONT' ? (el.getAttribute('color') ?? '') : '');
    if (el.dataset.c) l.c = el.dataset.c;
    else if (css && own && hexOf(css) === own) delete l.c;
    else if (css) {
      const colour = colourKey(css);
      if (colour) l.c = colour;
    }
    return l;
  };
  const visit = (node: Node, look: TextRun) => {
    node.childNodes.forEach((child, i) => {
      if (at && at.node === node && at.offset === i && pos < 0) pos = len;
      if (child.nodeType === Node.TEXT_NODE) {
        const t = (child as Text).data.replace(/\u00a0/g, ' ');
        if (t) settle(look);
        if (at && at.node === child && pos < 0) pos = len + Math.min(at.offset, t.length);
        push(t, look);
      } else if (child instanceof HTMLElement) {
        if (child.tagName === 'BR') {
          // (the last <br> of a line's block only holds it open: it is no line break of its own)
          if (followed(child)) {
            settle(look);
            push('\n', look);
          }
          return;
        }
        // a block begins a line: after words on the line, or after a block (an empty one too)
        const block = BLOCK.has(child.tagName);
        if (block) {
          if (broken) settle(look);
          else if (!endsLine()) push('\n', look);
        }
        visit(child, lookOf(child, look));
        if (block) broken = true;
      }
    });
    if (at && at.node === node && at.offset >= node.childNodes.length && pos < 0) pos = len;
  };
  visit(root, { t: '' });
  return { runs: mergeRuns(out), pos: pos < 0 ? len : pos };
}

/** Runs drawn into the field: each stretch its text in its look's elements, a line break a <br>. */
function draw(root: HTMLElement, runs: readonly TextRun[]): void {
  root.replaceChildren();
  for (const r of runs) {
    const frag = document.createDocumentFragment();
    r.t.split('\n').forEach((part, i) => {
      if (i) frag.append(document.createElement('br'));
      if (part) frag.append(document.createTextNode(part));
    });
    let node: Node = frag;
    const wrap = (tag: string) => {
      const el = document.createElement(tag);
      el.append(node);
      node = el;
      return el;
    };
    if (r.c) {
      const el = wrap('span');
      el.dataset.c = r.c;
      el.style.color = textColor(r.c) ?? '';
    }
    if (r.s) wrap('s');
    if (r.u) wrap('u');
    if (r.i) wrap('i');
    if (r.b) wrap('b');
    root.append(node);
  }
  // an empty last line shows only with something after its break
  if (plain(runs).endsWith('\n')) root.append(document.createElement('br'));
}

/** The DOM place of a place in the words (after a field is drawn): for a selection. */
function placeOf(root: HTMLElement, pos: number): { node: Node; offset: number } {
  let left = pos;
  let found: { node: Node; offset: number } | null = null;
  const visit = (node: Node): boolean => {
    for (const child of [...node.childNodes]) {
      if (child.nodeType === Node.TEXT_NODE) {
        const n = (child as Text).data.length;
        if (left <= n) {
          found = { node: child, offset: left };
          return true;
        }
        left -= n;
      } else if (child instanceof HTMLBRElement) {
        if (left === 0) {
          found = { node: child.parentNode!, offset: [...child.parentNode!.childNodes].indexOf(child) };
          return true;
        }
        left -= 1;
      } else if (visit(child)) return true;
    }
    return false;
  };
  visit(root);
  return found ?? { node: root, offset: root.childNodes.length };
}

/* ------------------------------------------------------------ the field */

export interface RichField {
  el: HTMLElement;
  /** What it holds, as typed. */
  runs(): TextRun[];
  /** What it holds, drawn again (the caret kept at the end when it had one). */
  set(runs: readonly TextRun[]): void;
  focus(): void;
  focused(): boolean;
  /** A look given to (or taken from) what is selected — at a caret, what is typed next; with no caret in the field, all of it. */
  toggle(k: Look): void;
  /** A colour of its own for what is selected (null: the label's own), as `toggle` — or for what was selected when `keep` was called (a colour chosen elsewhere takes the caret away). */
  colour(c: string | null): void;
  /** What is selected now, kept for the next `colour`. */
  keep(): void;
  /** Whether what is selected (or all of it) has a look. */
  has(k: Look): boolean;
  /** The one colour of what is selected (or all of it), null for the label's own (or more than one). */
  colourNow(): string | null;
}

export function richField(opts: { runs: readonly TextRun[]; placeholder: string; label: string; multiline?: boolean; onInput: (runs: TextRun[]) => void; onEnter?: () => void; onCaret?: () => void }): RichField {
  const el = h('div', { class: 'input rich', contenteditable: 'true', role: 'textbox', 'aria-label': opts.label, 'aria-multiline': String(opts.multiline !== false), spellcheck: 'true', autocapitalize: 'sentences', 'data-placeholder': opts.placeholder });
  const empty = () => el.classList.toggle('empty', !plain(walk(el).runs).trim() && !el.querySelector('br + br'));
  draw(el, opts.runs);
  empty();
  const selection = (): { from: number; to: number } | null => {
    const sel = document.getSelection();
    if (!sel || !sel.rangeCount || document.activeElement !== el) return null;
    const r = sel.getRangeAt(0);
    if (!el.contains(r.startContainer) || !el.contains(r.endContainer)) return null;
    const a = walk(el, { node: r.startContainer, offset: r.startOffset }).pos;
    const b = walk(el, { node: r.endContainer, offset: r.endOffset }).pos;
    return { from: Math.min(a, b), to: Math.max(a, b) };
  };
  const select = (from: number, to: number) => {
    const sel = document.getSelection();
    if (!sel) return;
    const a = placeOf(el, from),
      b = placeOf(el, to);
    const r = document.createRange();
    r.setStart(a.node, a.offset);
    r.setEnd(b.node, b.offset);
    sel.removeAllRanges();
    sel.addRange(r);
  };
  /**
   * A look chosen at a bare caret (`at`): for what is typed next from there, and on from the end of
   * that until the caret goes elsewhere — given on the runs, as every look is (the browser's own
   * typing style is not kept across a tap on a button). `looks`: what it makes of each look.
   */
  let pending: { at: number; fn: (r: TextRun) => TextRun; looks: Partial<Record<Look, boolean>> & { c?: string | null } } | null = null;
  /** How long the words were after the last change: what was typed is what they grew by. */
  let lastLen = plain(opts.runs).length;
  const changed = () => {
    let runs = walk(el).runs;
    const len = plain(runs).length;
    const sel = selection();
    // typed at the caret a look was chosen at (by keys, the pad, a paste): what was typed takes it
    if (pending && sel && sel.from === sel.to && len > lastLen) {
      const from = sel.to - (len - lastLen);
      if (from === pending.at) {
        runs = restyle(runs, from, sel.to, pending.fn);
        draw(el, runs);
        select(sel.to, sel.to);
        pending.at = sel.to;
      } else pending = null;
    } else if (pending && (!sel || sel.from !== pending.at)) pending = null;
    lastLen = len;
    empty();
    opts.onInput(runs);
  };
  el.addEventListener('input', changed);
  el.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    // one line: Return is done; several: Return is a new line, and ⌘/Ctrl-Return is done
    if (opts.multiline === false || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      opts.onEnter?.();
    } else {
      // a <br>, not a block: what is written is lines, not paragraphs
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  });
  // pasted words come plain, in the look where they land
  el.addEventListener('paste', (e) => {
    e.preventDefault();
    let t = e.clipboardData?.getData('text/plain') ?? '';
    if (opts.multiline === false) t = t.replace(/\s*\n\s*/g, ' ');
    document.execCommand('insertText', false, t);
  });
  el.addEventListener('drop', (e) => e.preventDefault());
  const onSel = () => {
    if (document.activeElement === el) opts.onCaret?.();
  };
  document.addEventListener('selectionchange', onSel);
  // (gone from the page, it listens no more)
  new MutationObserver((_, obs) => {
    if (!el.isConnected) {
      document.removeEventListener('selectionchange', onSel);
      obs.disconnect();
    }
  }).observe(document.body, { childList: true, subtree: true });

  /** What was selected when a colour began to be chosen (null: nothing of it was). */
  let kept: { from: number; to: number } | null = null;
  /** The caret, when it is one (a bare caret in the field), or null. */
  const caret = (): number | null => {
    const sel = selection();
    return sel && sel.from === sel.to ? sel.from : null;
  };
  const apply = (fn: (r: TextRun) => TextRun, looks: Partial<Record<Look, boolean>> & { c?: string | null }) => {
    const runs = walk(el).runs;
    let sel = selection();
    // (chosen elsewhere: the caret went with it — back to where it was)
    if (!sel && kept) {
      sel = kept;
      el.focus({ preventScroll: true });
      select(sel.from, sel.to);
    }
    kept = null;
    if (sel && sel.from === sel.to) {
      // a bare caret: what is typed next from here
      const was = pending && pending.at === sel.from ? pending : null;
      pending = { at: sel.from, fn: was ? (r) => fn(was.fn(r)) : fn, looks: { ...(was?.looks ?? {}), ...looks } };
      opts.onCaret?.();
      return;
    }
    const { from, to } = sel ?? { from: 0, to: plain(runs).length };
    const next = restyle(runs, from, to, fn);
    draw(el, next);
    if (sel) select(from, to);
    changed();
    opts.onCaret?.();
  };
  return {
    el,
    runs: () => walk(el).runs,
    set(runs) {
      const had = document.activeElement === el;
      if (plain(walk(el).runs) === plain(runs) && JSON.stringify(walk(el).runs) === JSON.stringify(mergeRuns(runs))) return;
      draw(el, runs);
      lastLen = plain(runs).length;
      pending = null;
      empty();
      if (had) select(plain(runs).length, plain(runs).length);
    },
    focus() {
      el.focus({ preventScroll: true });
      const n = plain(walk(el).runs).length;
      select(n, n);
    },
    focused: () => document.activeElement === el,
    toggle(k) {
      const on = !this.has(k);
      apply((r) => {
        const { [k]: _was, ...rest } = r;
        return on ? { ...rest, [k]: true } : (rest as TextRun);
      }, { [k]: on });
      haptic();
    },
    colour(c) {
      apply((r) => {
        const { c: _was, ...rest } = r;
        return c ? { ...rest, c } : (rest as TextRun);
      }, { c });
    },
    keep() {
      kept = selection();
    },
    has(k) {
      const runs = walk(el).runs;
      const at = caret();
      // at a caret: what is typed next would have it (a look chosen there; else the words before it)
      if (at !== null) return pending?.at === at && pending.looks[k] !== undefined ? !!pending.looks[k] : allHave(runs, at, at, k);
      const sel = selection();
      const { from, to } = sel ?? { from: 0, to: plain(runs).length };
      return allHave(runs, from, to, k);
    },
    colourNow() {
      const runs = walk(el).runs;
      const at = caret();
      if (at !== null && pending?.at === at && pending.looks.c !== undefined) return pending.looks.c;
      const sel = selection();
      const { from, to } = sel ?? { from: 0, to: plain(runs).length };
      return colourOf(runs, from, to);
    },
  };
}

/* ------------------------------------------------------------ the row of looks */

/** What a box's look (as a whole) is, and how it is changed: its size, where its lines sit, its width set by hand. */
export interface BoxControls {
  size(): number;
  setSize(k: number): void;
  align(): 'left' | 'center' | 'right';
  setAlign(a: 'left' | 'center' | 'right'): void;
  /** Its width was set by hand: Fit gives it back the width of its words. */
  fitted(): boolean;
  fit(): void;
}
const SIZES = [0.75, 1, 1.25, 1.5, 2];

/**
 * The looks of a label's words, in two short rows under its field: B I U S and the colour (on what is
 * selected, or all of it), then the box's size (− 100 % +), where its lines sit, and Fit for a width
 * set by hand. Every button keeps the field's caret and selection (a press on it takes no focus).
 */
export function lookRows(field: RichField | null, box: BoxControls | null, openColours: (anchor: HTMLElement, now: string | null, pick: (c: string | null) => void) => void, own = '#1a1a1a'): { toolbar: HTMLElement | null; box: HTMLElement | null; sync(): void } {
  const btn = (aria: string, child: Node, onClick: () => void, cls = '') => h('button', { class: `btn icon look ${cls}`.trim(), type: 'button', 'aria-label': aria, onClick }, child);
  const glyph = (t: string, cls: string) => h('span', { class: `glyph ${cls}`, 'aria-hidden': 'true' }, t);
  const looks: [Look, string, string][] = [
    ['b', 'Bold', 'B'],
    ['i', 'Italic', 'I'],
    ['u', 'Underline', 'U'],
    ['s', 'Strikethrough', 'S'],
  ];
  const lookBtns = field ? looks.map(([k, aria, t]) => btn(aria, glyph(t, k), () => (field.toggle(k), sync()))) : [];
  const dot = h('span', { class: 'dot', 'aria-hidden': 'true' });
  const colourBtn: HTMLButtonElement | null = field
    ? btn('Colour', dot, () => {
        haptic();
        field.keep();
        openColours(colourBtn!, field.colourNow(), (c) => (field.colour(c), sync()));
      })
    : null;
  const sizeLabel = h('span', { class: 'size-now' });
  const step = (dir: 1 | -1) => () => {
    if (!box) return;
    const now = box.size();
    const i = SIZES.findIndex((s) => Math.abs(s - now) < 0.01);
    const next = SIZES[Math.max(0, Math.min(SIZES.length - 1, (i < 0 ? 1 : i) + dir))]!;
    if (next !== now) box.setSize(next);
    haptic();
    sync();
  };
  const smaller = btn('Smaller', icon(ICONS.minus, 18), step(-1));
  const larger = btn('Larger', icon(ICONS.plus, 18), step(1));
  const aligns: ['left' | 'center' | 'right', string, string][] = [
    ['left', 'Align left', ICONS.alignLeft],
    ['center', 'Centre', ICONS.alignCenter],
    ['right', 'Align right', ICONS.alignRight],
  ];
  const alignBtns = aligns.map(([a, aria, ic]) => btn(aria, icon(ic, 18), () => (box?.setAlign(a), haptic(), sync())));
  const fitBtn = h('button', { class: 'btn look fit', type: 'button', 'aria-label': 'Fit', title: 'As wide as its words', onClick: () => (box?.fit(), haptic(), sync()) }, icon(ICONS.fitWidth, 18), h('span', { class: 'label' }, 'Fit'));
  // the words' looks: a toolbar along the top of their field (what it acts on is right under it)
  const toolbar = field ? h('div', { class: 'looks words', role: 'group', 'aria-label': 'Look of the words' }, ...lookBtns, h('span', { class: 'divider', 'aria-hidden': 'true' }), colourBtn) : null;
  // the box's: its size, where its lines sit, its width again as its words', in groups of their own
  const boxRow = box
    ? h('div', { class: 'looks box', role: 'group', 'aria-label': 'Size and alignment' },
        h('div', { class: 'group stepper' }, smaller, sizeLabel, larger),
        h('div', { class: 'group align' }, ...alignBtns),
        fitBtn)
    : null;
  // (a press on a button keeps the caret where it is: the keyboard stays, the selection with it)
  for (const el of [toolbar, boxRow]) {
    el?.addEventListener('mousedown', (e) => e.preventDefault());
    el?.addEventListener('pointerdown', (e) => {
      if (field?.focused()) e.preventDefault();
    });
  }
  const sync = () => {
    if (field) {
      looks.forEach(([k], i) => lookBtns[i]!.setAttribute('aria-pressed', String(field.has(k))));
      const c = field.colourNow();
      // (the colour its words are drawn in on the plan: its own, unless given another)
      dot.style.background = textColor(c ?? undefined) ?? own;
      colourBtn!.classList.toggle('own', !!c);
    }
    if (box) {
      const k = box.size();
      sizeLabel.textContent = `${Math.round(k * 100)}%`;
      smaller.disabled = k <= SIZES[0]! + 0.001;
      larger.disabled = k >= SIZES[SIZES.length - 1]! - 0.001;
      const a = box.align();
      aligns.forEach(([v], i) => alignBtns[i]!.setAttribute('aria-pressed', String(v === a)));
      fitBtn.hidden = !box.fitted();
    }
  };
  sync();
  return { toolbar, box: boxRow, sync };
}
